import type { Commodity } from '@/features/inventorization/domain/commodity';
import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { CAPTURE_SESSION_STATUSES, type CaptureSession } from '@/shared/capture/capture-session';
import type { MeterSearchResult } from '@/shared/meter-identification';
import { normalizeSearchField } from '@/shared/meter-identification';

export const METER_LIST_ITEM_KINDS = {
  cachedMeter: 'cached_meter',
  session: 'session',
} as const;

export type MeterListItem =
  | {
      id: string;
      kind: typeof METER_LIST_ITEM_KINDS.session;
      session: CaptureSession;
    }
  | {
      id: string;
      kind: typeof METER_LIST_ITEM_KINDS.cachedMeter;
      meter: MeterSearchResult;
    };

export function createCombinedMeterList(sessions: CaptureSession[], meters: MeterSearchResult[]): MeterListItem[] {
  const cachedMeterKeys = new Set<string>();

  for (const meter of meters) {
    if (meter.data._id) {
      cachedMeterKeys.add(meter.data._id);
    }

    if (meter.data.id) {
      cachedMeterKeys.add(String(meter.data.id));
    }
  }

  const visibleSessions = deduplicateSessionsByBackendMeter(
    sessions.filter(session => !isSyncedDuplicateSession(session, cachedMeterKeys)),
  );
  const sessionMongoIds = new Set(visibleSessions.map(session => session._id).filter(Boolean));
  const sessionBackendIds = new Set(visibleSessions.map(item => item.meterId).filter(Boolean));
  const sessionRows: MeterListItem[] = visibleSessions.map(session => ({
    id: `session:${session.id}`,
    kind: METER_LIST_ITEM_KINDS.session,
    session,
  }));
  const fallbackRows: MeterListItem[] = meters
    .filter(meter => {
      if (meter.data._id && sessionMongoIds.has(meter.data._id)) {
        return false;
      }

      if (meter.data.id && sessionBackendIds.has(meter.data.id)) {
        return false;
      }

      return true;
    })
    .map(meter => ({
      id: `cached_meter:${meter.id}`,
      kind: METER_LIST_ITEM_KINDS.cachedMeter,
      meter,
    }));

  return [...sessionRows, ...fallbackRows];
}

export function filterMeters(items: MeterListItem[], query: string) {
  const normalizedQuery = normalizeSearchField(query);

  if (!normalizedQuery) {
    return items;
  }

  return items.filter(item =>
    getSearchableValues(item).some(value => readNormalizedValue(value)?.includes(normalizedQuery)),
  );
}

export function filterMetersByCommodity(items: MeterListItem[], commodity: Commodity | '') {
  if (!commodity) {
    return items;
  }

  return items.filter(item => {
    if (item.kind === METER_LIST_ITEM_KINDS.session) {
      return readInventorizationIdentity(item.session).commodity === commodity;
    }

    return item.meter.data.commodity === commodity;
  });
}

export function openMeterListItem(
  item: MeterListItem,
  onOpenQueuedMeter: (sessionId: string) => void,
  onOpenMeter: (meter: MeterSearchResult) => Promise<void> | void,
) {
  if (item.kind === METER_LIST_ITEM_KINDS.session) {
    onOpenQueuedMeter(item.session.id);
    return;
  }

  void onOpenMeter(item.meter);
}

function getSearchableValues(item: MeterListItem) {
  if (item.kind === METER_LIST_ITEM_KINDS.cachedMeter) {
    const { meter } = item;

    return [
      meter.data.apiKey,
      meter.data.buildingName,
      meter.data.floorName,
      meter.data._id,
      meter.data.id?.toString(),
      meter.data.combinedMeterId,
      meter.data.commodity,
      meter.data.name,
      meter.data.ownerName,
      meter.data.serial,
      meter.data.status,
      meter.data.tenant,
      meter.data.toolsId,
    ];
  }

  const context = readInventorizationContext(item.session);
  const identity = readInventorizationIdentity(item.session);

  return [
    item.session.meterId?.toString(),
    item.session._id,
    item.session.id,
    identity.apiKey,
    context.floorName,
    context.buildingName,
    identity.commodity,
    context.ownerName,
    identity.serial,
    context.tenantName,
    item.session.status,
  ];
}

function readNormalizedValue(value: unknown) {
  if (value === null || typeof value === 'undefined') {
    return undefined;
  }

  return normalizeSearchField(String(value));
}

function isSyncedDuplicateSession(session: CaptureSession, cachedMeterKeys: Set<string>) {
  if (session.status !== CAPTURE_SESSION_STATUSES.synced) {
    return false;
  }

  return [session._id, session.meterId?.toString()].some(key => key && cachedMeterKeys.has(key));
}

function deduplicateSessionsByBackendMeter(sessions: CaptureSession[]) {
  const sessionByBackendMeter = new Map<string, CaptureSession>();
  const sessionsWithoutBackendMeter: CaptureSession[] = [];

  for (const session of sessions) {
    const key = session.meterId?.toString() ?? session._id;

    if (!key) {
      sessionsWithoutBackendMeter.push(session);
      continue;
    }

    const existing = sessionByBackendMeter.get(key);

    if (!existing || session.updatedAt > existing.updatedAt) {
      sessionByBackendMeter.set(key, session);
    }
  }

  return [...sessionsWithoutBackendMeter, ...sessionByBackendMeter.values()];
}
